// @ts-check
import { Cell, Pie, PieChart } from 'recharts';
import BlockCard from '../BlockCard';
import { ORDER_STATUS, ORDER_STATUS_ORDER, orderStatusLabel } from '../../../utils/orderStatus';

// One colour per status; unknown statuses fall back to grey.
const STATUS_COLOR = {
  [ORDER_STATUS.PENDING]: '#f59e0b',
  [ORDER_STATUS.CONFIRMED]: '#3b82f6',
  [ORDER_STATUS.PROCESSING]: '#8b5cf6',
  [ORDER_STATUS.SHIPPED]: '#06b6d4',
  [ORDER_STATUS.DELIVERED]: '#22c55e',
  [ORDER_STATUS.CANCELLED]: '#9ca3af',
};
const FALLBACK_COLOR = '#6b7280';

const formatRupees = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

/** Orders by status donut with a legend, plus paid revenue for the range and its change on the previous period. */
export default function OrdersBlock({ block }) {
  const { by_status: byStatus = {}, revenue = 0, revenue_delta_pct: delta = null } = block.data || {};
  const known = ORDER_STATUS_ORDER.filter((s) => byStatus[s]);
  const extra = Object.keys(byStatus).filter((s) => !ORDER_STATUS_ORDER.includes(s) && byStatus[s]);
  const slices = [...known, ...extra].map((s) => ({ status: s, label: orderStatusLabel(s), value: byStatus[s] }));
  const rounded = delta === null || delta === undefined ? null : Math.round(delta);

  return (
    <BlockCard title="Orders" href={block.href} headline={block.headline}>
      <div className="flex items-center gap-4">
        {slices.length > 0 && (
          <PieChart width={120} height={120} aria-hidden="true">
            <Pie data={slices} dataKey="value" nameKey="label" innerRadius={34} outerRadius={56} stroke="none" isAnimationActive={false}>
              {slices.map((s) => (
                <Cell key={s.status} fill={STATUS_COLOR[s.status] || FALLBACK_COLOR} />
              ))}
            </Pie>
          </PieChart>
        )}
        <ul className="flex-1 space-y-1 text-sm" data-testid="orders-legend">
          {slices.length === 0 && <li className="text-muted-foreground">No orders yet</li>}
          {slices.map((s) => (
            <li key={s.status} className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: STATUS_COLOR[s.status] || FALLBACK_COLOR }} />
                {s.label}
              </span>
              <span className="font-medium">{s.value}</span>
            </li>
          ))}
        </ul>
      </div>
      <p className="mt-4 text-sm text-muted-foreground" data-testid="orders-revenue">
        Revenue <span className="font-semibold text-foreground">{formatRupees(revenue)}</span>
        <span data-testid="orders-revenue-delta" className="ml-2">
          {rounded === null ? '—' : `${rounded > 0 ? '+' : ''}${rounded}%`}
        </span>
      </p>
    </BlockCard>
  );
}
